import Toolbar from "@mui/material/Toolbar";
import { Avatar, Button, CardMedia, IconButton } from "@mui/material";
import { Box } from "@mui/system";
import AppBar from "@mui/material/AppBar";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ciCustomColors } from "@/theme/colors";
import logo_stacked from "@/assets/ci-logo-stacked-nav.svg";

import I18nSelect from "@/features/translation";
import { useAuth } from "@/features/auth";
import TrialSubscription from "../trial-subscription";
import { SubscribeButtonNavigation } from "../subscribe-button-navigation";
import UserMenu from "../user-menu";
import { NAVIGATION_HEIGHT } from "../../consts";
import { NavigationVariant } from "../../types";

export const AppNavbar = ({ variant }: { variant: NavigationVariant }) => {
  const { user, logout, isLogoutPending } = useAuth();
  const { t } = useTranslation("coi-platform-translations");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const logoutButton = () => {
    if (isLogoutPending || variant === "reduced") {
      return null;
    }
    return (
      <>
        <I18nSelect variant="dark" />
        <Button onClick={() => logout()} data-testid="onboarding__logout-button" disabled={isLogoutPending}>
          {t("navigation.logout_button")}
        </Button>
      </>
    );
  };

  const logo = (
    <CardMedia
      component="img"
      image={logo_stacked}
      alt="Cookie Information Logo"
      sx={{
        maxWidth: {
          xs: "100px",
          md: "140px",
        },
        objectFit: "contain",
      }}
    />
  );

  return (
    <AppBar
      color="secondary"
      position="fixed"
      sx={{
        height: NAVIGATION_HEIGHT,
        display: "flex",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderBottom: `2px solid ${ciCustomColors.blue[50]}`,
        boxShadow: "none",
        px: "34px",
      }}
    >
      <Toolbar disableGutters>
        {variant === "full" || (variant === "reduced" && !!user?.current_user_id) ? (
          <Link to="/consent-solutions">{logo}</Link>
        ) : (
          logo
        )}
        <Box
          sx={(theme) => ({
            gap: theme.spacing(3),
            flexGrow: 1,
            flexDirection: "row",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          })}
        >
          {variant === "full" || (variant === "reduced" && !!user?.current_user_id) ? (
            <>
              <TrialSubscription />
              <SubscribeButtonNavigation />
              <I18nSelect variant="dark" />
              <IconButton
                aria-label={t("navigation.user_menu_button.aria_label") as string}
                data-testid="user-menu-settings"
                onClick={openMenu}
                sx={{ p: 0 }}
              >
                <Avatar sx={{ bgcolor: "primary.main" }} />
              </IconButton>
              <UserMenu anchorEl={anchorEl} closeMenu={closeMenu} />
            </>
          ) : (
            logoutButton()
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
